import { Controller, useForm } from 'react-hook-form';
import { Flex, Text, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { isNil } from 'ramda';

import asBentoPage from 'common/bento/hoc/asBentoPage';
import * as Form from 'components/_core/form';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';
import OBStickyButton from 'features/Onboarding/components/OBStickyButton/OBStickyButton';

import Header from '../../../../components/Header';
import businessRoleSchema, { BusinessRoleSchema } from './businessRoleSchema';
import locales from './locales';

interface BusinessRoleProps {
  onContinue: (payload: BusinessRoleSchema) => void;
  isSending: boolean;
  defaultValues: Partial<BusinessRoleSchema>;
}

const BusinessRole = asBentoPage<BusinessRoleProps>(
  ({ defaultValues, isSending, Layout, onContinue, onPrev }) => {
    const {
      control,
      formState: { isValid },
      handleSubmit,
    } = useForm<BusinessRoleSchema>({
      defaultValues,
      mode: 'onChange',
      resolver: zodResolver(businessRoleSchema),
    });

    return (
      <Layout nav={<OBNavigation onPrev={onPrev} />}>
        <Flex
          as="form"
          direction="column"
          height={{ base: 'full', md: 'auto' }}
          onSubmit={handleSubmit((values) => onContinue(values))}
        >
          <Header title={locales.title} />

          <VStack
            align="stretch"
            height={{ base: 'full', md: 'auto' }}
            spacing="space-40"
          >
            <VStack align="flex-start" spacing="space-12">
              <Text fontWeight="weight-500" variant="primary">
                {locales.directorLabel.present}
              </Text>

              <Text>{locales.directorDescription.present}</Text>

              <Controller
                control={control}
                name="isDirector"
                render={({ field: { onChange, value } }) => (
                  <Form.Radio.Group
                    direction="row"
                    onChange={(val) => onChange(val === 'true')}
                    radioPosition="right"
                    value={isNil(value) ? undefined : String(value)}
                    variant="outline"
                  >
                    <Form.Radio value="true">
                      <Form.Radio.Title>{locales.yes}</Form.Radio.Title>
                    </Form.Radio>

                    <Form.Radio value="false">
                      <Form.Radio.Title>{locales.no}</Form.Radio.Title>
                    </Form.Radio>
                  </Form.Radio.Group>
                )}
              />
            </VStack>

            <VStack align="flex-start" spacing="space-12">
              <Text fontWeight="weight-500" variant="primary">
                {locales.majorShareholderLabel.present}
              </Text>

              <Text>{locales.majorShareholderDescription.present}</Text>

              <Controller
                control={control}
                name="isMajorShareholder"
                render={({ field: { onChange, value } }) => (
                  <Form.Radio.Group
                    direction="row"
                    onChange={(val) => onChange(val === 'true')}
                    radioPosition="right"
                    value={isNil(value) ? undefined : String(value)}
                    variant="outline"
                  >
                    <Form.Radio value="true">
                      <Form.Radio.Title>{locales.yes}</Form.Radio.Title>
                    </Form.Radio>

                    <Form.Radio value="false">
                      <Form.Radio.Title>{locales.no}</Form.Radio.Title>
                    </Form.Radio>
                  </Form.Radio.Group>
                )}
              />
            </VStack>
          </VStack>

          <OBStickyButton
            isDisabled={!isValid}
            isLoading={isSending}
            type="submit"
          >
            {locales.continueCta}
          </OBStickyButton>
        </Flex>
      </Layout>
    );
  },
);

export default BusinessRole;
